import {
  MANAGE_VIP_STATUS_FAIL,
  MANAGE_VIP_STATUS_MESSAGE,
  MANAGE_VIP_STATUS_SUCCESS,
  MANAGE_VIP_STATUS_TITLE
} from '@bo-constants/member/memberCopy';
import { useCallback, useMemo } from 'react';
import { Combobox } from '@library/components/selects';
import { MemberConfirmationModal } from '@bo-components/members/modal';
import { PrizepicksSwitch } from '@library/components/switches';
import { classNames } from '@library/utils';
import { isConfirmationErrorMessage } from '@bo-utils/members/memberCommon';
import { useMemberVipStatus } from '@bo-hooks/members';

export interface MemberManageVipStatusModalProps {
  isVipMember: boolean;
  memberId: string;
  onClose: () => void;
  vipHostAssignedId?: string;
}

export const MemberManageVipStatusModal = ({
  isVipMember,
  memberId,
  onClose,
  vipHostAssignedId = undefined
}: MemberManageVipStatusModalProps) => {
  const {
    isLoading,
    isSubmitError,
    isSubmitSuccess,
    isVipStatusActive,
    onSubmit,
    onVipHostChange,
    onVipStatusChange,
    selectedVipHost,
    vipHostComboboxOptions
  } = useMemberVipStatus({ isVipMember, vipHostAssignedId });

  const showConfirmationMessage = isSubmitError || isSubmitSuccess;
  const disableVipHostCombobox = isLoading || !isVipStatusActive;
  const disableConfirmButton = isVipStatusActive && !selectedVipHost;

  const handleSubmit = useCallback(async () => {
    await onSubmit?.(memberId);
  }, [memberId, onSubmit]);

  const UpdateVipStatus = useMemo(() => {
    return (
      <div className="text-soClean-180 flex flex-col text-sm">
        <p>{MANAGE_VIP_STATUS_MESSAGE}</p>
        <div className="flex flex-row items-center justify-between py-6">
          <span>VIP status</span>
          <PrizepicksSwitch
            active={isVipStatusActive}
            disabled={isLoading}
            handleChange={onVipStatusChange}
          />
        </div>
        <Combobox
          closeMenuOnSelect
          disabled={disableVipHostCombobox}
          id="memberManageVipStatus"
          isMulti={false}
          label="Add new host"
          labelClassName="text-soClean-180 text-sm leading-8"
          onChange={onVipHostChange}
          optionClassName="font-heebo text-sm px-3 py-1 rounded bg-soFresh-110 capitalize"
          options={vipHostComboboxOptions}
          placeholder="Select VIP host"
          value={selectedVipHost}
        />
      </div>
    );
  }, [
    disableVipHostCombobox,
    isLoading,
    isVipStatusActive,
    onVipHostChange,
    onVipStatusChange,
    selectedVipHost,
    vipHostComboboxOptions
  ]);

  const ConfirmationMessage = useMemo(() => {
    const confirmationMessage = isSubmitSuccess
      ? MANAGE_VIP_STATUS_SUCCESS
      : MANAGE_VIP_STATUS_FAIL;
    const isErrorMessage = isConfirmationErrorMessage(confirmationMessage);

    return (
      <p
        className={classNames(
          'text-sm',
          isErrorMessage ? 'text-roses' : 'text-soClean-180'
        )}
      >
        {confirmationMessage}
      </p>
    );
  }, [isSubmitSuccess]);

  return (
    <MemberConfirmationModal
      cancelCopy="Cancel"
      childrenClassName="justify-evenly min-h-32"
      className="w-[25rem]"
      confirmationCopy="Save"
      handleConfirm={handleSubmit}
      isConfirmDisabled={disableConfirmButton}
      isOpen
      label={MANAGE_VIP_STATUS_TITLE}
      onCancelModal={onClose}
      showConfirmationMessage={showConfirmationMessage}
    >
      {!showConfirmationMessage ? UpdateVipStatus : ConfirmationMessage}
    </MemberConfirmationModal>
  );
};
